import Joi from "joi-browser";
import Lottie from "lottie-react";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import * as fileloading from "../../animations/file-folder.json";
import clientProgramOptionSets from "../../options/clientprogram.json";
import fosterCareHomeBedOptionSets from "../../options/fostercarehomebed.json";
import {
  crisisReferrals,
  getAddresses,
  getAllergies,
  getBedAppointments,
  getCareModalities,
  getCareTeam,
  getClientDiagnosisDetails,
  getClientIdentifiers,
  getClientPayors,
  getClientProgramIdentifiers,
  getClientPrograms,
  getFosterBeds,
  getMedications,
  getPermanencyStatuses,
  soarUpdateApi,
  writeErrorLog,
} from "../services/soar";
import {
  getFieldDate,
  getFieldName,
  getFieldText,
  updateEntity,
} from "./../../scripts/helperScripts";
import Checkbox from "./Checkbox";
import Input from "./Input";
import RadioButton from "./RadioButton";
import Select from "./Select";

function DischargeChecklistModal({
  clientId,
  clientProgram,
  recordedServices,
}) {
  const [dischargeData, setDischargeData] = useState({
    dischargeDate: "",
    dischargeReason: "",
    adoptionType: "",
    safyRequest: "",
    safyRequestedDischarge: "",
    dischargeFosterHomeProgram: false,
    fosterProgramDischargeReason: "186460000",
    fosterBedDischargeReason: "",
    clientIdentifiersToExpire: [],
    clientPayorsToExpire: [],
    allergiesToExpire: [],
    medicationsToExpire: [],
    addressesToExpire: [],
    permanencyStatusesToExpire: [],
    diagnosesToExpire: [],
  });
  const [isOpen, setIsOpen] = useState(false);
  const [careTeam, setCareTeam] = useState([]);
  const [diagnosisDetails, setDiagnosisDetails] = useState([]);
  const [careModalities, setCareModalities] = useState([]); // 5
  const [clientProgramIdentifiers, setClientProgramIdentifiers] = useState([]);
  const [clientPrograms, setClientPrograms] = useState([]);
  const [bedAppointments, setBedAppointments] = useState([]);
  const [fosterHomeProgramToDischarge, setFosterHomeProgramToDischarge] =
    useState();
  const [fosterHomeBeds, setFosterHomeBeds] = useState([]); // 10
  const [
    fosterHomeProgramDiagnosisDetails,
    setFosterHomeProgramDiagnosisDetails,
  ] = useState([]);
  const [fosterHomeProgramDiagnoses, setFosterHomeProgramDiagnoses] = useState(
    []
  );
  const [fosterHomeProgramIdentifiers, setFosterHomeProgramIdentifiers] =
    useState([]);
  const [clientPayors, setClientPayors] = useState([]);
  const [allergies, setAllergies] = useState([]); // 15
  const [medications, setMedications] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [permanencyStatuses, setPermanencyStatuses] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [clientIdentifiers, setClientIdentifiers] = useState([]); // 20
  const [careTeamLoaded, setCareTeamLoaded] = useState(false);
  const [clientDiagnosisDetailsLoaded, setClientDiagnosisDetailsLoaded] =
    useState(false);
  const [careModalitiesLoaded, setCareModalitiesLoaded] = useState(false);
  const [clientProgramIdentifiersLoaded, setClientProgramIdentifiersLoaded] =
    useState(false);
  const [clientProgramsLoaded, setClientProgramsLoaded] = useState(false); // 25
  const [bedAppointmentsLoaded, setBedAppointmentsLoaded] = useState(false);
  const [fosterHomeBedsLoaded, setFosterHomeBedsLoaded] = useState(false);
  const [
    fosterHomeProgramDiagnosisDetailsLoaded,
    setFosterHomeProgramDiagnosisDetailsLoaded,
  ] = useState(false);
  const [
    fosterHomeProgramDiagnosesLoaded,
    setFosterHomeProgramDiagnosesLoaded,
  ] = useState(false);
  const [
    fosterHomeProgramIdentifiersLoaded,
    setFosterHomeProgramIdentifiersLoaded, // 30
  ] = useState(false);
  const [clientPayorsLoaded, setClientPayorsLoaded] = useState(false);
  const [allergiesLoaded, setAllergiesLoaded] = useState(false);
  const [medicationsLoaded, setMedicationsLoaded] = useState(false);
  const [addressesLoaded, setAddressesLoaded] = useState(false);
  const [permanencyStatusesLoaded, setPermanencyStatusesLoaded] = // 35
    useState(false);
  const [diagnosesLoaded, setDiagnosesLoaded] = useState(false);
  const [clientIdentifiersLoaded, setClientIdentifiersLoaded] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const map = new Map();
  const orgUrl = process.env.REACT_APP_ORG;
  const orgApi = orgUrl.replace("https://", "").replace(".safy.org", "");
  let toastId;

  const schema = useMemo(() => {
    const admitDate = moment(getFieldDate(clientProgram.ccx_admitdate));

    return {
      dischargeDate: Joi.date()
        .required()
        .greater(admitDate.clone().subtract(1, "day").toDate())
        .label("Discharge Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.greater":
                error.message = `"Discharge Date" cannot be before ${admitDate.format(
                  "L"
                )}`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      dischargeReason:
        getFieldText(clientProgram["unit.ccx_programtype"]) !== "186460002"
          ? Joi.string().required().label("Discharge Reason")
          : Joi.string().allow(""),
      adoptionType:
        getFieldText(clientProgram["unit.ccx_programtype"]) === "186460003"
          ? Joi.when("dischargeReason", {
              is: "186460002",
              then: Joi.string().required().label("Adoption Type"),
            })
          : Joi.string().allow(""),
      safyRequest:
        getFieldText(clientProgram["unit.ccx_programtype"]) === "186460003" &&
        getFieldName(clientProgram.ccx_company) === "SAFY of Alabama"
          ? Joi.string().required().label("Did SAFY request discharge?")
          : Joi.string().allow(""),
      safyRequestedDischarge:
        getFieldText(clientProgram["unit.ccx_programtype"]) === "186460003" &&
        getFieldName(clientProgram.ccx_company) === "SAFY of Alabama"
          ? Joi.when("safyRequest", {
              is: "1",
              then: Joi.string().required().label("SAFY Requested Discharge"),
            })
          : Joi.string().allow(""),
      dischargeFosterHomeProgram: Joi.boolean(),
      fosterProgramDischargeReason: Joi.when("dischargeFosterHomeProgram", {
        is: true,
        then: Joi.string().required().label("Program Discharge Reason"),
      }),
      fosterBedDischargeReason:
        fosterHomeBeds.length > 0
          ? Joi.when("dischargeFosterHomeProgram", {
              is: true,
              then: Joi.string().required().label("Bed Discharge Reason"),
            })
          : Joi.string().allow(""),
      clientIdentifiersToExpire: Joi.array().items(
        Joi.object({
          _id: Joi.string(),
          name: Joi.string(),
          entityId: Joi.string(),
          entityName: Joi.string(),
          friendlyName: Joi.string(),
        })
      ),
      clientPayorsToExpire: Joi.array().items(
        Joi.object({
          _id: Joi.string(),
          name: Joi.string(),
          entityId: Joi.string(),
          entityName: Joi.string(),
          friendlyName: Joi.string(),
        })
      ),
      allergiesToExpire: Joi.array().items(
        Joi.object({
          _id: Joi.string(),
          name: Joi.string(),
          entityId: Joi.string(),
          entityName: Joi.string(),
          friendlyName: Joi.string(),
        })
      ),
      medicationsToExpire: Joi.array().items(
        Joi.object({
          _id: Joi.string(),
          name: Joi.string(),
          entityId: Joi.string(),
          entityName: Joi.string(),
          friendlyName: Joi.string(),
        })
      ),
      addressesToExpire: Joi.array().items(
        Joi.object({
          _id: Joi.string(),
          name: Joi.string(),
          entityId: Joi.string(),
          entityName: Joi.string(),
          friendlyName: Joi.string(),
        })
      ),
      permanencyStatusesToExpire: Joi.array().items(
        Joi.object({
          _id: Joi.string(),
          name: Joi.string(),
          entityId: Joi.string(),
          entityName: Joi.string(),
          friendlyName: Joi.string(),
        })
      ),
      diagnosesToExpire: Joi.array().items(
        Joi.object({
          _id: Joi.string(),
          name: Joi.string(),
          entityId: Joi.string(),
          entityName: Joi.string(),
          friendlyName: Joi.string(),
        })
      ),
    };
  }, [clientProgram, fosterHomeBeds.length]);

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const recordedServiceExists = useMemo(() => {
    if (
      !dischargeData.dischargeDate ||
      !clientProgram["unit.ccx_recordserviceondischargedate"] ||
      recordedServices.length === 0
    ) {
      return false;
    }

    const date = moment(dischargeData.dischargeDate);
    const dateToCheck =
      getFieldText(clientProgram["unit.ccx_recordserviceondischargedate"]) ===
      "1"
        ? date.add(1, "day")
        : date;

    const activeRecordedServicesAfterDischarge = recordedServices.filter(
      (service) =>
        moment(getFieldText(service.ccx_endtime)).isAfter(dateToCheck)
    );

    return activeRecordedServicesAfterDischarge.length > 0;
  }, [dischargeData.dischargeDate, clientProgram, recordedServices]);

  const isLoaded = () => {
    return (
      careTeamLoaded &&
      clientDiagnosisDetailsLoaded &&
      careModalitiesLoaded &&
      clientProgramIdentifiersLoaded &&
      clientProgramsLoaded &&
      bedAppointmentsLoaded &&
      fosterHomeBedsLoaded &&
      fosterHomeProgramDiagnosisDetailsLoaded &&
      fosterHomeProgramDiagnosesLoaded &&
      fosterHomeProgramIdentifiersLoaded &&
      clientPayorsLoaded &&
      allergiesLoaded &&
      medicationsLoaded &&
      addressesLoaded &&
      permanencyStatusesLoaded &&
      diagnosesLoaded &&
      clientIdentifiersLoaded
    );
  };

  const getRecordsToDischarge = () => {
    getCareTeam(clientId).then((response) => {
      if (isMountedRef.current) {
        if (response) {
          const careTeamMembers = response
            .filter(
              (ct) =>
                getFieldText(ct.ccx_clientprogram) ===
                  clientProgram.ccx_client_programid &&
                ct.ccx_expdate === undefined
            )
            .map((ct) => ({
              friendlyName: "Care Team",
              entityName: "ccx_careteam",
              entityId: ct.ccx_careteamid,
              name: ct.ccx_name,
            }));

          setCareTeam(careTeamMembers);
        }

        setCareTeamLoaded(true);
      }
    });

    getClientDiagnosisDetails(clientId).then((response) => {
      if (isMountedRef.current) {
        if (response) {
          const dxDetailsForClientProgram = response.filter(
            (dxDetail) =>
              getFieldText(dxDetail.ccx_clientprogram) ===
                clientProgram.ccx_client_programid &&
              dxDetail.ccx_expdate === undefined
          );

          const dxDetails = dxDetailsForClientProgram.map((dxDetail) => ({
            friendlyName: "Diagnosis Detail",
            entityName: "ccx_clientdiagnosisdetail",
            entityId: dxDetail.ccx_clientdiagnosisdetailid,
            name: dxDetail.ccx_name,
          }));

          setDiagnosisDetails(dxDetails);

          const dxRecords = dxDetailsForClientProgram
            .filter(
              (dxDetail) =>
                response.filter(
                  (detail) =>
                    getFieldText(detail.ccx_clientdiagnosis) ===
                      getFieldText(dxDetail.ccx_clientdiagnosis) &&
                    detail.ccx_expdate === undefined
                ).length === 1 && dxDetail.ccx_expdate === undefined
            )
            .map((dxDetail) => ({
              _id: getFieldText(dxDetail.ccx_clientdiagnosis),
              name: getFieldName(dxDetail.ccx_clientdiagnosis),
              friendlyName: "Diagnosis",
              entityName: "ccx_clientdiagnosis",
              entityId: getFieldText(dxDetail.ccx_clientdiagnosis),
            }));

          setDiagnoses(dxRecords);
        }

        setClientDiagnosisDetailsLoaded(true);
        setDiagnosesLoaded(true);
      }
    });

    getCareModalities(clientId).then((response) => {
      if (isMountedRef.current) {
        setCareModalitiesLoaded(true);
        if (response) {
          const modalities = response
            .filter((modality) => modality.ccx_enddate === undefined)
            .map((modality) => ({
              friendlyName: "Care Modality",
              entityName: "ccx_caremodality",
              entityId: modality.ccx_caremodalityid,
              name: getFieldName(modality.ccx_caremodality),
              clientProgramId: getFieldText(modality.ccx_clientprogramid),
            }));

          setCareModalities(modalities);
        }
      }
    });

    getClientPrograms(clientId).then((response) => {
      if (response && isMountedRef.current) {
        const otherPrograms = response.filter(
          (cp) =>
            cp.ccx_client_programid !== clientProgram.ccx_client_programid &&
            cp.ccx_dischargedate === undefined
        );

        setClientPrograms(otherPrograms);

        if (getFieldName(clientProgram["unit.ccx_programtype"]) === "TFC") {
          const fosterHomeProgram = otherPrograms.filter(
            (program) =>
              getFieldName(program["unit.ccx_programtype"]) === "Foster Home" &&
              getFieldText(program["unit.ccx_facility"]) ===
                getFieldText(clientProgram["unit.ccx_facility"])
          )[0];

          setFosterHomeProgramToDischarge(fosterHomeProgram);

          if (fosterHomeProgram) {
            getFosterBeds(clientId).then((response2) => {
              if (isMountedRef.current) {
                setFosterHomeBedsLoaded(true);
                if (response2) {
                  const fosterBeds = response2
                    .filter(
                      (bed) =>
                        getFieldText(bed.ccx_clientprogram) ===
                          fosterHomeProgram.ccx_client_programid &&
                        bed.ccx_actualdischarge === undefined
                    )
                    .map((bed) => ({
                      friendlyName: "Foster Home Bed",
                      entityName: "ccx_fosterchild",
                      entityId: bed.ccx_fosterchildid,
                      name: bed.ccx_name,
                    }));

                  setFosterHomeBeds(fosterBeds);
                }
              }
            });

            getClientDiagnosisDetails(clientId).then((response2) => {
              if (isMountedRef.current) {
                setFosterHomeProgramDiagnosisDetailsLoaded(true);
                setFosterHomeProgramDiagnosesLoaded(true);
                if (response2 && isMountedRef.current) {
                  const dxDetailsForClientProgram = response2.filter(
                    (dxDetail) =>
                      getFieldText(dxDetail.ccx_clientprogram) ===
                        fosterHomeProgram.ccx_client_programid &&
                      dxDetail.ccx_expdate === undefined
                  );

                  const dxDetails = dxDetailsForClientProgram.map(
                    (dxDetail) => ({
                      friendlyName: "Diagnosis Detail",
                      entityName: "ccx_clientdiagnosisdetail",
                      entityId: dxDetail.ccx_clientdiagnosisdetailid,
                      name: dxDetail.ccx_name,
                    })
                  );

                  setFosterHomeProgramDiagnosisDetails(dxDetails);

                  const dxRecords = dxDetailsForClientProgram
                    .filter(
                      (dxDetail) =>
                        response2.filter(
                          (detail) =>
                            getFieldText(detail.ccx_clientdiagnosis) ===
                              getFieldText(dxDetail.ccx_clientdiagnosis) &&
                            detail.ccx_expdate === undefined
                        ).length === 1 && dxDetail.ccx_expdate === undefined
                    )
                    .map((dxDetail) => ({
                      friendlyName: "Client Diagnosis",
                      entityName: "ccx_clientdiagnosis",
                      entityId: getFieldText(dxDetail.ccx_clientdiagnosis),
                      name: getFieldName(dxDetail.ccx_clientdiagnosis),
                    }));

                  setFosterHomeProgramDiagnoses(dxRecords);
                }
              }
            });

            getClientProgramIdentifiers(
              fosterHomeProgram.ccx_client_programid,
              safyInfo.user.safyInfo.BusinessUnitIdName
            ).then((response2) => {
              if (isMountedRef.current) {
                setFosterHomeProgramIdentifiersLoaded(true);
                if (response2) {
                  const identifiers = response2
                    .filter(
                      (identifier) => identifier.ccx_expdate === undefined
                    )
                    .map((identifier) => ({
                      friendlyName: "Client Program Identifier",
                      entityName: "ccx_clientprogramidentifier",
                      entityId: identifier.ccx_clientprogramidentifierid,
                      name: identifier.ccx_name,
                    }));

                  setFosterHomeProgramIdentifiers(identifiers);
                }
              }
            });
          } else {
            setFosterHomeProgramDiagnosisDetailsLoaded(true);
            setFosterHomeBedsLoaded(true);
            setFosterHomeProgramDiagnosesLoaded(true);
            setFosterHomeProgramIdentifiersLoaded(true);
          }
        } else {
          setFosterHomeProgramDiagnosisDetailsLoaded(true);
          setFosterHomeBedsLoaded(true);
          setFosterHomeProgramDiagnosesLoaded(true);
          setFosterHomeProgramIdentifiersLoaded(true);
        }
      }

      setClientProgramsLoaded(true);
    });

    getBedAppointments(clientId).then((response) => {
      if (isMountedRef.current) {
        setBedAppointmentsLoaded(true);
        if (response) {
          const bedAppts = response
            .filter(
              (ba) =>
                getFieldText(ba.ccx_clientprogram) ===
                  clientProgram.ccx_client_programid &&
                ba.ccx_actualdischargedate === undefined
            )
            .map((ba) => ({
              friendlyName: "Bed Appointment",
              entityName: "ccx_bedappointment",
              entityId: ba.ccx_bedappointmentid,
              name: ba.ccx_name,
            }));

          setBedAppointments(bedAppts);
        }
      }
    });

    getClientPayors(clientId).then((response) => {
      if (isMountedRef.current) {
        setClientPayorsLoaded(true);
        if (response) {
          const payors = response
            .filter((payor) => payor.ccx_expdate === undefined)
            .map((payor) => ({
              _id: payor.ccx_clientpayorid,
              name: `${payor.ccx_name} - ${getFieldDate(payor.ccx_effdate)}`,
              entityId: payor.ccx_clientpayorid,
              entityName: "ccx_clientpayor",
              friendlyName: "Payor",
            }));

          setClientPayors(payors);
        }
      }
    });

    getAllergies(clientId).then((response) => {
      if (isMountedRef.current) {
        setAllergiesLoaded(true);
        if (response) {
          const alrgs = response
            .filter((allergy) => allergy.ccx_expdate === undefined)
            .map((allergy) => ({
              _id: allergy.ccx_client_allergyid,
              name: allergy.ccx_allergystr,
              entityId: allergy.ccx_client_allergyid,
              entityName: "ccx_client_allergy",
              friendlyName: "Allergy",
            }));
          setAllergies(alrgs);
        }
      }
    });

    getMedications(clientId).then((response) => {
      if (isMountedRef.current) {
        setMedicationsLoaded(true);
        if (response) {
          const meds = response
            .filter((med) => med.ccx_stopdate === undefined)
            .map((med) => ({
              _id: med.ccx_clientmedicationid,
              name: med.ccx_name,
              entityId: med.ccx_clientmedicationid,
              entityName: "ccx_clientmedication",
              friendlyName: "Medication",
            }));

          setMedications(meds);
        }
      }
    });

    getAddresses(clientId).then((response) => {
      if (isMountedRef.current) {
        setAddressesLoaded(true);
        if (response) {
          const addressRecords = response
            .filter((address) => address.ccx_expirationdate === undefined)
            .map((address) => ({
              _id: address.ccx_contactaddressid,
              name: `${address.ccx_line1}, ${address.ccx_city}, ${
                address.ccx_state
              } ${address.ccx_postalcode} - ${getFieldDate(
                address.ccx_effectivedate
              )}`,
              entityId: address.ccx_contactaddressid,
              entityName: "ccx_contactaddress",
              friendlyName: "Address",
            }));

          setAddresses(addressRecords);
        }
      }
    });

    getPermanencyStatuses(clientId).then((response) => {
      if (isMountedRef.current) {
        setPermanencyStatusesLoaded(true);
        if (response) {
          const statuses = response
            .filter((status) => status.svc_expirationdate === undefined)
            .map((status) => ({
              _id: status.svc_permanencystatusid,
              name: status.svc_name,
              entityId: status.svc_permanencystatusid,
              entityName: "svc_permanencystatus",
              friendlyName: "Permanency Status",
            }));

          setPermanencyStatuses(statuses);
        }
      }
    });

    getClientIdentifiers(clientId).then((response) => {
      if (isMountedRef.current) {
        setClientIdentifiersLoaded(true);
        if (response) {
          const identifiers = response
            .filter((identifier) => {
              const identifierType = getFieldText(
                identifier.ccx_clientidentifiertype
              );

              return (
                identifier.ccx_expdate === undefined &&
                identifierType !== "186460001" &&
                identifierType !== "186460002"
              );
            })
            .map((identifier) => ({
              _id: identifier.ccx_clientidentifiersid,
              name: identifier.ccx_name,
              entityId: identifier.ccx_clientidentifiersid,
              entityName: "ccx_clientidentifiers",
              friendlyName: "Client Identifier",
            }));

          setClientIdentifiers(identifiers);
        }
      }
    });

    getClientProgramIdentifiers(
      clientProgram.ccx_client_programid,
      safyInfo.user.safyInfo.BusinessUnitIdName
    ).then((response) => {
      if (isMountedRef.current) {
        setClientProgramIdentifiersLoaded(true);
        if (response) {
          const identifiers = response
            .filter((identifier) => {
              const identifierType = getFieldText(
                identifier.ccx_clientidentifiertype
              );

              return (
                identifier.ccx_expdate === undefined &&
                identifierType !== "186460001" &&
                identifierType !== "186460002"
              );
            })
            .map((identifier) => ({
              entityId: identifier.ccx_clientprogramidentifierid,
              name: identifier.ccx_name,
              friendlyName: "Client Program Identifier",
              entityName: "ccx_clientprogramidentifier",
            }));

          setClientProgramIdentifiers(identifiers);
        }
      }
    });
  };

  const getCareModalitiesToDischarge = () => {
    const modalities = careModalities.filter(
      (modality) =>
        modality.clientProgramId === clientProgram.ccx_client_programid
    );

    if (
      fosterHomeProgramToDischarge &&
      dischargeData.dischargeFosterHomeProgram
    ) {
      const fosterHomeModalities = careModalities.filter(
        (modality) =>
          modality.clientProgramId ===
          fosterHomeProgramToDischarge.ccx_client_programid
      );
      return modalities.concat(fosterHomeModalities);
    }

    return modalities;
  };

  const validate = (dataObj = {}) => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(dataObj, schema, options);
    if (!error) return true;

    let validationErrors = {};
    for (let item of error.details)
      validationErrors[item.path[0]] = validationErrors[item.path[0]] ? (
        (validationErrors[item.path[0]] = (
          <>
            {validationErrors[item.path[0]]}
            <br />
            <>{item.message}</>
          </>
        ))
      ) : (
        <>{item.message}</>
      );

    console.log("errors: ", validationErrors);
    setErrors(validationErrors);
    return false;
  };

  const validateProperty = (input) => {
    const obj = {
      [input.name]:
        input.type === "checkbox" ? input.checked : input.value || undefined,
    };
    const objSchema = { [input.name]: schema[input.name] };
    const { error } = Joi.validate(obj, objSchema);

    setErrors((errs) => {
      const obj = { ...errs };

      if (error) {
        obj[input.name] = error?.details[0].message;
      } else {
        delete obj[input.name];
      }

      return obj;
    });
  };

  const handleChange = ({ currentTarget: input }) => {
    validateProperty(input);
    setDischargeData((d) => {
      const obj = { ...d };
      obj[input.name] = input.type === "checkbox" ? input.checked : input.value;
      return obj;
    });

    if (input.name === "dischargeReason" && input.value !== "186460002") {
      setErrors((err) => {
        const obj = { ...err };
        delete obj.adoptionType;
        return obj;
      });
    }
  };

  const handleArrayChange = (e, item, propertyName) => {
    let items = dischargeData[propertyName]
      ? [...dischargeData[propertyName]]
      : [];

    if (e.currentTarget.checked) items.push(item);
    else items = items.filter((x) => x._id !== item._id);

    setDischargeData((d) => {
      const obj = { ...d };
      obj[propertyName] = items;
      return obj;
    });
  };

  const handleShowModal = () => {
    setIsOpen(true);
    getRecordsToDischarge();
  };

  const handleHideModal = () => {
    setErrors({});
    setDischargeData({
      dischargeDate: "",
      dischargeReason: "",
      adoptionType: "",
      safyRequest: "",
      safyRequestedDischarge: "",
      dischargeFosterHomeProgram: false,
      fosterProgramDischargeReason: "186460000",
      fosterBedDischargeReason: "",
      clientIdentifiersToExpire: [],
      clientPayorsToExpire: [],
      allergiesToExpire: [],
      medicationsToExpire: [],
      addressesToExpire: [],
      permanencyStatusesToExpire: [],
      diagnosesToExpire: [],
    });
    setIsOpen(false);
  };

  const handleSave = () => {
    toastId = toast.loading("Discharging Client...", { type: toast.TYPE.INFO });

    const dischargeDateVal = new Date(dischargeData.dischargeDate);

    // set time to 23:59
    const clientProgramDischargeDate = new Date(
      dischargeDateVal.getTime() + 23 * 60 * 60 * 1000 + 59 * 60 * 1000
    );

    const expireLog = [];

    careTeam.forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: item.entityName,
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);

      const obj = {
        ccx_expdate: { type: "datetime", value: dischargeDateVal },
      };

      updateEntity("ccx_careteam", item.entityId, JSON.stringify(obj), safyInfo)
        .then(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    });

    diagnosisDetails.forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: item.entityName,
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);

      const obj = {
        ccx_expdate: { type: "datetime", value: dischargeDateVal },
      };

      updateEntity(
        "ccx_clientdiagnosisdetail",
        item.entityId,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    });

    getCareModalitiesToDischarge().forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: item.entityName,
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);

      const obj = {
        ccx_enddate: { type: "datetime", value: dischargeDateVal },
      };

      updateEntity(
        "ccx_caremodality",
        item.entityId,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    });

    bedAppointments.forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: item.entityName,
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);

      const obj = {
        ccx_actualdischargedate: { type: "datetime", value: dischargeDateVal },
        ccx_servicestatuslookup: {
          type: "entity",
          value: {
            entityName: "ccx_servicestatus",
            entityId: "{315A3B59-0AEB-E211-90BC-005056822045}", // Discharged
          },
        },
      };

      updateEntity(
        "ccx_bedappointment",
        item.entityId,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    });

    clientProgramIdentifiers.forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: item.entityName,
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);

      const obj = {
        ccx_expdate: { type: "datetime", value: dischargeDateVal },
      };

      updateEntity(
        "ccx_clientprogramidentifier",
        item.entityId,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    });

    if (dischargeData.dischargeFosterHomeProgram) {
      fosterHomeBeds.forEach((item) => {
        map.set(item.entityId, {
          name: item.name,
          entityName: item.entityName,
          friendlyName: item.friendlyName,
          inProgress: true,
          updateSuccessful: false,
        });

        const expireLogItem = {
          entityName: item.entityName,
          entityId: item.entityId,
          name: item.name,
          expirationDate: dischargeDateVal,
          modifiedBy: safyInfo.user.domainName,
        };

        expireLog.push(expireLogItem);

        const obj = {
          ccx_actualdischarge: {
            type: "datetime",
            value: dischargeDateVal,
          },
          ccx_expirationdate: {
            type: "datetime",
            value: dischargeDateVal,
          },
          ccx_servicestatuslookup: {
            type: "entity",
            value: {
              entityName: "ccx_servicestatus",
              entityId: "{315A3B59-0AEB-E211-90BC-005056822045}", // Discharged
            },
          },
          svc_dischargetransferreason: {
            type: "optionset",
            value: parseInt(dischargeData.fosterBedDischargeReason),
          },
        };

        updateEntity(
          "ccx_fosterchild",
          item.entityId,
          JSON.stringify(obj),
          safyInfo
        )
          .then(() => {
            map.set(expireLogItem.entityId, {
              ...map.get(expireLogItem.entityId),
              inProgress: false,
              updateSuccessful: true,
            });
          })
          .catch(() => {
            map.set(expireLogItem.entityId, {
              ...map.get(expireLogItem.entityId),
              inProgress: false,
              updateSuccessful: false,
            });
          })
          .finally(() => {
            updateToast();
          });
      });

      fosterHomeProgramDiagnosisDetails.forEach((item) => {
        map.set(item.entityId, {
          name: item.name,
          entityName: item.entityName,
          friendlyName: item.friendlyName,
          inProgress: true,
          updateSuccessful: false,
        });

        const expireLogItem = {
          entityName: item.entityName,
          entityId: item.entityId,
          name: item.name,
          expirationDate: dischargeDateVal,
          modifiedBy: safyInfo.user.domainName,
        };

        expireLog.push(expireLogItem);

        const obj = {
          ccx_expdate: {
            type: "datetime",
            value: new Date(
              dischargeDateVal.getFullYear(),
              dischargeDateVal.getMonth() + 1,
              dischargeDateVal.getDate()
            ),
          },
        };

        updateEntity(
          "ccx_clientdiagnosisdetail",
          item.entityId,
          JSON.stringify(obj),
          safyInfo
        )
          .then(() => {
            map.set(expireLogItem.entityId, {
              ...map.get(expireLogItem.entityId),
              inProgress: false,
              updateSuccessful: true,
            });
          })
          .catch(() => {
            map.set(expireLogItem.entityId, {
              ...map.get(expireLogItem.entityId),
              inProgress: false,
              updateSuccessful: false,
            });
          })
          .finally(() => {
            updateToast();
          });
      });

      fosterHomeProgramDiagnoses.forEach((item) => {
        map.set(item.entityId, {
          name: item.name,
          entityName: item.entityName,
          friendlyName: item.friendlyName,
          inProgress: true,
          updateSuccessful: false,
        });

        const expireLogItem = {
          entityName: item.entityName,
          entityId: item.entityId,
          name: item.name,
          expirationDate: dischargeDateVal,
          modifiedBy: safyInfo.user.domainName,
        };

        expireLog.push(expireLogItem);

        const obj = {
          ccx_expdate: { type: "datetime", value: dischargeDateVal },
        };

        updateEntity(
          "ccx_clientdiagnosis",
          item.entityId,
          JSON.stringify(obj),
          safyInfo
        )
          .then(() => {
            map.set(expireLogItem.entityId, {
              ...map.get(expireLogItem.entityId),
              inProgress: false,
              updateSuccessful: true,
            });
          })
          .catch(() => {
            map.set(expireLogItem.entityId, {
              ...map.get(expireLogItem.entityId),
              inProgress: false,
              updateSuccessful: false,
            });
          })
          .finally(() => {
            updateToast();
          });
      });

      fosterHomeProgramIdentifiers.forEach((item) => {
        map.set(item.entityId, {
          name: item.name,
          entityName: item.entityName,
          friendlyName: item.friendlyName,
          inProgress: true,
          updateSuccessful: false,
        });

        const expireLogItem = {
          entityName: item.entityName,
          entityId: item.entityId,
          name: item.name,
          expirationDate: dischargeDateVal,
          modifiedBy: safyInfo.user.domainName,
        };

        expireLog.push(expireLogItem);

        const obj = {
          ccx_expdate: { type: "datetime", value: dischargeDateVal },
        };

        updateEntity(
          "ccx_clientprogramidentifier",
          item.entityId,
          JSON.stringify(obj),
          safyInfo
        )
          .then(() => {
            map.set(expireLogItem.entityId, {
              ...map.get(expireLogItem.entityId),
              inProgress: false,
              updateSuccessful: true,
            });
          })
          .catch(() => {
            map.set(expireLogItem.entityId, {
              ...map.get(expireLogItem.entityId),
              inProgress: false,
              updateSuccessful: false,
            });
          })
          .finally(() => {
            updateToast();
          });
      });

      map.set(fosterHomeProgramToDischarge.ccx_client_programid, {
        name: fosterHomeProgramToDischarge.ccx_name,
        entityName: "ccx_client_program",
        friendlyName: "Foster Home Client Program",
        inProgress: true,
        updateSuccessful: false,
      });

      const fosterProgramExpireLogItem = {
        entityName: "ccx_client_program",
        entityId: fosterHomeProgramToDischarge.ccx_client_programid,
        name: fosterHomeProgramToDischarge.ccx_name,
        expirationDate: clientProgramDischargeDate,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(fosterProgramExpireLogItem);

      const obj = {
        ccx_dischargedate: {
          type: "datetime",
          value: clientProgramDischargeDate,
        },
        ccx_administrativeclosedate: {
          type: "datetime",
          value: new Date(
            dischargeDateVal.getFullYear(),
            dischargeDateVal.getMonth() + 1,
            dischargeDateVal.getDate()
          ),
        },
        sor_dischargereasonfosterhome: {
          type: "optionset",
          value: parseInt(dischargeData.fosterProgramDischargeReason),
        },
      };

      updateEntity(
        "ccx_client_program",
        fosterHomeProgramToDischarge.ccx_client_programid,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          map.set(fosterProgramExpireLogItem.entityId, {
            ...map.get(fosterProgramExpireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(fosterProgramExpireLogItem.entityId, {
            ...map.get(fosterProgramExpireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    }

    dischargeData.clientIdentifiersToExpire.forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: "ccx_clientidentifiers",
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);

      const obj = {
        ccx_expdate: { type: "datetime", value: dischargeDateVal },
      };

      updateEntity(
        "ccx_clientidentifiers",
        item.entityId,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    });

    dischargeData.clientPayorsToExpire.forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: "ccx_clientpayor",
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);

      const obj = {
        ccx_expdate: { type: "datetime", value: dischargeDateVal },
      };

      updateEntity(
        "ccx_clientpayor",
        item.entityId,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    });

    dischargeData.allergiesToExpire.forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: "ccx_client_allergy",
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);

      const obj = {
        ccx_expdate: { type: "datetime", value: dischargeDateVal },
      };

      updateEntity(
        "ccx_client_allergy",
        item.entityId,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    });

    dischargeData.medicationsToExpire.forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: "ccx_clientmedication",
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);
    });

    expireMeds(dischargeData.medicationsToExpire, dischargeDateVal);

    dischargeData.addressesToExpire.forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: "ccx_contactaddress",
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);

      const obj = {
        ccx_expirationdate: {
          type: "datetime",
          value: dischargeDateVal,
        },
      };

      updateEntity(
        "ccx_contactaddress",
        item.entityId,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    });

    dischargeData.permanencyStatusesToExpire.forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: "svc_permanencystatus",
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);

      const obj = {
        svc_expirationdate: {
          type: "datetime",
          value: dischargeDateVal,
        },
      };

      updateEntity(
        "svc_permanencystatus",
        item.entityId,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    });

    dischargeData.diagnosesToExpire.forEach((item) => {
      map.set(item.entityId, {
        name: item.name,
        entityName: item.entityName,
        friendlyName: item.friendlyName,
        inProgress: true,
        updateSuccessful: false,
      });

      const expireLogItem = {
        entityName: "ccx_clientdiagnosis",
        entityId: item.entityId,
        name: item.name,
        expirationDate: dischargeDateVal,
        modifiedBy: safyInfo.user.domainName,
      };

      expireLog.push(expireLogItem);

      const obj = {
        ccx_expdate: { type: "datetime", value: dischargeDateVal },
      };

      updateEntity(
        "ccx_clientdiagnosis",
        item.entityId,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(expireLogItem.entityId, {
            ...map.get(expireLogItem.entityId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          updateToast();
        });
    });

    const obj = {
      ccx_dischargedate: {
        type: "datetime",
        value: clientProgramDischargeDate,
      },
      sor_dischargelog: { type: "string", value: JSON.stringify(expireLog) },
      ccx_administrativeclosedate: {
        type: "datetime",
        value: new Date(
          dischargeDateVal.getFullYear(),
          dischargeDateVal.getMonth() + 1,
          dischargeDateVal.getDate()
        ),
      },
    };

    if (getFieldText(clientProgram["unit.ccx_programtype"]) === "186460000") {
      obj.sor_dischargereasonoys = {
        type: "optionset",
        value: parseInt(dischargeData.dischargeReason),
      };
      obj.sor_dischargereasontext = {
        type: "string",
        value: clientProgramOptionSets.sor_dischargereasonoys.filter(
          (item) => item._id === dischargeData.dischargeReason
        )[0].name,
      };
    } else if (
      getFieldText(clientProgram["unit.ccx_programtype"]) === "186460001"
    ) {
      obj.sor_dischargereasonsbehavioralhealth = {
        type: "optionset",
        value: parseInt(dischargeData.dischargeReason),
      };
      obj.sor_dischargereasontext = {
        type: "string",
        value:
          clientProgramOptionSets.sor_dischargereasonsbehavioralhealth.filter(
            (item) => item._id === dischargeData.dischargeReason
          )[0].name,
      };
    } else if (
      getFieldText(clientProgram["unit.ccx_programtype"]) === "186460003"
    ) {
      obj.sor_dischargereasontfc = {
        type: "optionset",
        value: parseInt(dischargeData.dischargeReason),
      };
      obj.sor_dischargereasontext = {
        type: "string",
        value: clientProgramOptionSets.sor_dischargereasontfc.filter(
          (item) => item._id === dischargeData.dischargeReason
        )[0].name,
      };

      if (dischargeData.dischargeReason === "186460002") {
        obj.sor_adoptiontype = {
          type: "optionset",
          value: parseInt(dischargeData.adoptionType),
        };
      }

      if (getFieldName(clientProgram.ccx_company) === "SAFY of Alabama") {
        obj.sor_safyrequest = {
          type: "bool",
          value: dischargeData.safyRequest === "1",
        };

        if (dischargeData.safyRequest === "1") {
          obj.sor_safyrequesteddischarge = {
            type: "optionset",
            value:
              dischargeData.safyRequestedDischarge &&
              parseInt(dischargeData.safyRequestedDischarge),
          };
        }
      }
    } else if (
      getFieldText(clientProgram["unit.ccx_programtype"]) === "186460004"
    ) {
      obj.sor_dischargereasonfamilypreservation = {
        type: "optionset",
        value: parseInt(dischargeData.dischargeReason),
      };
      obj.sor_dischargereasontext = {
        type: "string",
        value:
          clientProgramOptionSets.sor_dischargereasonfamilypreservation.filter(
            (item) => item._id === dischargeData.dischargeReason
          )[0].name,
      };
    } else if (
      getFieldText(clientProgram["unit.ccx_programtype"]) === "186460005"
    ) {
      obj.sor_dischargereasonfosterhome = {
        type: "optionset",
        value: parseInt(dischargeData.dischargeReason),
      };
      obj.sor_dischargereasontext = {
        type: "string",
        value: clientProgramOptionSets.sor_dischargereasonfosterhome.filter(
          (item) => item._id === dischargeData.dischargeReason
        )[0].name,
      };
    } else if (
      getFieldText(clientProgram["unit.ccx_programtype"]) === "186460006"
    ) {
      obj.sor_dischargereasonfpp = {
        type: "optionset",
        value: parseInt(dischargeData.dischargeReason),
      };
      obj.sor_dischargereasontext = {
        type: "string",
        value: clientProgramOptionSets.sor_dischargereasonfpp.filter(
          (item) => item._id === dischargeData.dischargeReason
        )[0].name,
      };
    }

    soarUpdateApi(
      `SOAR/Post/${orgApi}/${safyInfo.user.domainName.replace(
        "safy\\",
        ""
      )}/ccx_client_program/${clientProgram.ccx_client_programid}`,
      JSON.stringify(obj),
      1
    );

    if (
      getFieldName(clientProgram.ccx_programunit).includes(
        "Mobile Crisis Response"
      )
    ) {
      const queryBody = {
        command: "query",
        application:
          process.env.REACT_APP_ENVIRONMENT === "Production"
            ? "5DAE139E-D052-4BD8-BF4A-499A31AC3C15"
            : "58990F83-8566-4BA8-B4B5-584A64E4CBA1",
        keyword: "byclientprogram",
        ids: [clientProgram.ccx_client_programid],
      };

      const updateReferral = async () => {
        const safyUser = JSON.parse(localStorage.getItem("safyInfo")).user;

        try {
          const response = await crisisReferrals(queryBody);
          if (response?.data?.[0]) {
            const updateBody = {
              ...response.data[0],
              command: "update",
              status: "Closed",
              modifiedBy: safyUser.azureId,
              modifiedByName: safyUser.displayName,
            };

            try {
              crisisReferrals(updateBody);
            } catch (updateError) {
              const org =
                process.env.REACT_APP_ENVIRONMENT === "Production"
                  ? "ReactLive"
                  : "ReactDev";
              const logData = {
                Who: `${safyUser.domainName}|${safyUser.displayName}`,
                FullError: JSON.stringify(
                  updateError,
                  Object.getOwnPropertyNames(updateError)
                ),
                API: "Zeto API",
                APIBody: JSON.stringify(updateBody),
                URL: "https://safyzetoapi.azurewebsites.net/crisis/referrals",
                Org: org,
                ResponseCode: null,
                ResponseMessage: updateError.message,
                ExceptionType: null,
                Application: "React Portal",
              };

              writeErrorLog(logData);
            }
          }
        } catch (queryError) {
          const org =
            process.env.REACT_APP_ENVIRONMENT === "Production"
              ? "ReactLive"
              : "ReactDev";
          const logData = {
            Who: `${safyUser.domainName}|${safyUser.displayName}`,
            FullError: JSON.stringify(
              queryError,
              Object.getOwnPropertyNames(queryError)
            ),
            API: "Zeto API",
            APIBody: JSON.stringify(queryBody),
            URL: "https://safyzetoapi.azurewebsites.net/crisis/referrals",
            Org: org,
            ResponseCode: null,
            ResponseMessage: queryError.message,
            ExceptionType: null,
            Application: "React Portal",
          };

          writeErrorLog(logData);
        }
      };

      updateReferral();
    }

    handleHideModal();
    navigate(-1);
  };

  const expireMeds = (medications, dischargeDateVal, index = 0) => {
    if (medications.length > 0) {
      const medId = medications[index].entityId;

      const obj = {
        ccx_stopdate: { type: "datetime", value: dischargeDateVal },
      };

      updateEntity("ccx_clientmedication", medId, JSON.stringify(obj), safyInfo)
        .then(() => {
          map.set(medId, {
            ...map.get(medId),
            inProgress: false,
            updateSuccessful: true,
          });
        })
        .catch(() => {
          map.set(medId, {
            ...map.get(medId),
            inProgress: false,
            updateSuccessful: false,
          });
        })
        .finally(() => {
          if (medications.length > index + 1)
            expireMeds(medications, dischargeDateVal, index + 1);
          else updateToast();
        });
    }
  };

  const updateToast = () => {
    const updateFailures = [];

    for (const item of map) {
      if (item[1].inProgress === true) return;
      if (item[1].updateSuccessful === false) updateFailures.push(item[1]);
    }

    if (updateFailures.length === 0)
      toast.update(toastId, {
        render: "Client has been discharged.",
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
      });
    else
      toast.update(toastId, {
        render: (
          <div className="p-2">
            The following records failed to discharge:
            <ul>
              {updateFailures.map((item, index) => {
                if (index < 10) {
                  return (
                    <li key={`updateFailures.${index}`}>
                      {item.friendlyName} - {item.name}
                    </li>
                  );
                }

                return null;
              })}
            </ul>
            {updateFailures.length > 10
              ? "Other records failed to dicharge."
              : null}
          </div>
        ),
        type: toast.TYPE.ERROR,
        isLoading: false,
        autoClose: 30000,
        closeOnClick: true,
      });
  };

  const getOptions = () => {
    const programType = getFieldText(clientProgram["unit.ccx_programtype"]);

    switch (programType) {
      case "186460000":
        return clientProgramOptionSets.sor_dischargereasonoys;
      case "186460001":
        return clientProgramOptionSets.sor_dischargereasonsbehavioralhealth;
      case "186460003":
        return clientProgramOptionSets.sor_dischargereasontfc;
      case "186460004":
        return clientProgramOptionSets.sor_dischargereasonfamilypreservation;
      case "186460005":
        return clientProgramOptionSets.sor_dischargereasonfosterhome;
      case "186460006":
        return clientProgramOptionSets.sor_dischargereasonfpp;
      default:
        return;
    }
  };

  return (
    <>
      <button className="btn btn-primary border mx-2" onClick={handleShowModal}>
        Client Discharge
      </button>

      {isOpen ? (
        <Modal show={isOpen} animation={false}>
          <Modal.Header>
            <Modal.Title>Client Discharge</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!isLoaded() ? (
              <div className="d-flex justify-content-center align-items-center">
                <h1>LOADING Client Discharge</h1>
                <Lottie
                  height={120}
                  width={120}
                  loop={true}
                  autoplay={true}
                  animationData={fileloading.default}
                  rendererSettings={{
                    preserveAspectRatio: "xMidYMid slice",
                  }}
                />
              </div>
            ) : (
              <>
                <Input
                  name="dischargeDate"
                  label="Discharge Date"
                  value={dischargeData.dischargeDate}
                  type="date"
                  onChange={handleChange}
                  error={errors.dischargeDate}
                ></Input>
                {recordedServiceExists && (
                  <div className="text-danger">
                    Client cannot be discharged on this date because one or more
                    Recorded Services exist after selected date
                  </div>
                )}
                {dischargeData.dischargeDate ? (
                  <>
                    {" "}
                    {getFieldText(clientProgram["unit.ccx_programtype"]) !==
                    "186460002" ? (
                      <>
                        <Select
                          name="dischargeReason"
                          label="Discharge Reason"
                          value={dischargeData.dischargeReason}
                          options={getOptions()}
                          onChange={handleChange}
                          error={errors.dischargeReason}
                        ></Select>
                        {getFieldText(clientProgram["unit.ccx_programtype"]) ===
                          "186460003" && (
                          <>
                            {dischargeData.dischargeReason === "186460002" && (
                              <Select
                                name="adoptionType"
                                label="Adoption Type"
                                value={dischargeData.adoptionType}
                                options={
                                  clientProgramOptionSets.sor_adoptiontype
                                }
                                onChange={handleChange}
                                error={errors.adoptionType}
                              ></Select>
                            )}
                            {getFieldName(clientProgram.ccx_company) ===
                              "SAFY of Alabama" && (
                              <>
                                <RadioButton
                                  name="safyRequest"
                                  label="Did SAFY Request Discharge?"
                                  value={dischargeData.safyRequest}
                                  options={
                                    clientProgramOptionSets.sor_safyrequest
                                  }
                                  onChange={handleChange}
                                  error={errors.safyRequest}
                                ></RadioButton>
                                {dischargeData.safyRequest === "1" && (
                                  <Select
                                    name="safyRequestedDischarge"
                                    label="SAFY Requested Discharge"
                                    value={dischargeData.safyRequestedDischarge}
                                    options={
                                      clientProgramOptionSets.sor_safyrequesteddischarge
                                    }
                                    onChange={handleChange}
                                    error={errors.safyRequestedDischarge}
                                  ></Select>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </>
                    ) : null}
                    <div className="border border-danger rounded p-2 mb-2">
                      <strong>
                        The following records will be discharged/expired
                        {dischargeData.dischargeDate
                          ? ` with the date ${moment
                              .utc(dischargeData.dischargeDate)
                              .format("L")}:`
                          : ":"}
                      </strong>
                      <ul>
                        <li key="clientProgramli">{clientProgram.ccx_name}</li>
                        {careTeam.map((record) => {
                          return (
                            <li
                              key={`careTeam.${record.entityId}`}
                            >{`${record.friendlyName} - ${record.name}`}</li>
                          );
                        })}
                        {bedAppointments.map((record) => {
                          return (
                            <li
                              key={`bedAppointments.${record.entityId}`}
                            >{`${record.friendlyName} - ${record.name}`}</li>
                          );
                        })}
                        {diagnosisDetails.map((record) => {
                          return (
                            <li
                              key={`diagnosisDetails.${record.entityId}`}
                            >{`${record.friendlyName} - ${record.name}`}</li>
                          );
                        })}
                        {dischargeData.dischargeFosterHomeProgram &&
                          fosterHomeProgramDiagnosisDetails.map((record) => {
                            return (
                              <li
                                key={`fosterHomeProgramDiagnosisDetails.${record.entityId}`}
                              >{`${record.friendlyName} - ${record.name}`}</li>
                            );
                          })}
                        {dischargeData.dischargeFosterHomeProgram &&
                          fosterHomeProgramDiagnoses.map((record) => {
                            return (
                              <li
                                key={`fosterHomeProgramDiagnoses.${record.entityId}`}
                              >{`${record.friendlyName} - ${record.name}`}</li>
                            );
                          })}
                        {getCareModalitiesToDischarge().map((record) => {
                          return (
                            <li
                              key={`getCareModalitiesToDischarge.${record.entityId}`}
                            >{`${record.friendlyName} - ${record.name}`}</li>
                          );
                        })}
                        {clientProgramIdentifiers.map((record) => {
                          return (
                            <li
                              key={`clientProgramIdentifiers.${record.entityId}`}
                            >{`${record.friendlyName} - ${record.name}`}</li>
                          );
                        })}
                        {dischargeData.dischargeFosterHomeProgram &&
                          fosterHomeProgramIdentifiers.map((record) => {
                            return (
                              <li
                                key={`fosterHomeProgramIdentifiers.${record.entityId}`}
                              >{`${record.friendlyName} - ${record.name}`}</li>
                            );
                          })}
                        {dischargeData.dischargeFosterHomeProgram &&
                          fosterHomeBeds.map((record) => {
                            return (
                              <li
                                key={`fosterHomeBeds.${record.entityId}`}
                              >{`${record.friendlyName} - ${record.name}`}</li>
                            );
                          })}
                      </ul>
                    </div>
                    {clientPrograms.length > 0 && (
                      <div className="my-2">
                        <strong>
                          The Client is also admitted to the following programs:
                        </strong>
                        {clientPrograms.map((program) => {
                          if (
                            getFieldName(
                              clientProgram["unit.ccx_programtype"]
                            ) === "TFC" &&
                            getFieldName(program["unit.ccx_programtype"]) ===
                              "Foster Home" &&
                            getFieldText(program["unit.ccx_facility"]) ===
                              getFieldText(clientProgram["unit.ccx_facility"])
                          ) {
                            return (
                              <Checkbox
                                name="dischargeFosterHomeProgram"
                                label={`${getFieldName(
                                  program.ccx_programunit
                                )} - ${moment(
                                  getFieldDate(program.ccx_admitdate)
                                ).format("L")}`}
                                divClassName="row ms-4 mt-2"
                                checked={
                                  dischargeData.dischargeFosterHomeProgram
                                }
                                onChange={handleChange}
                                key={`${program.ccx_client_program_id}`}
                              ></Checkbox>
                            );
                          } else {
                            return (
                              <div
                                className="ps-4"
                                key={program.ccx_client_program_id}
                              >
                                {getFieldName(program.ccx_programunit)} -{" "}
                                {moment(
                                  getFieldDate(program.ccx_admitdate)
                                ).format("L")}
                              </div>
                            );
                          }
                        })}
                        {dischargeData.dischargeFosterHomeProgram && (
                          <div className="border border-danger rounded p-2">
                            <Select
                              name="fosterProgramDischargeReason"
                              label="Program Discharge Reason"
                              value={dischargeData.fosterProgramDischargeReason}
                              options={
                                clientProgramOptionSets.sor_dischargereasonfosterhome
                              }
                              onChange={handleChange}
                              error={errors.fosterProgramDischargeReason}
                            ></Select>
                            {fosterHomeBeds.length > 0 && (
                              <Select
                                name="fosterBedDischargeReason"
                                label="Bed Discharge Reason"
                                value={dischargeData.fosterBedDischargeReason}
                                options={
                                  fosterCareHomeBedOptionSets.svc_dischargetransferreason
                                }
                                onChange={handleChange}
                                error={errors.fosterBedDischargeReason}
                              ></Select>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {clientIdentifiers.length > 0 ||
                    clientPayors.length > 0 ||
                    allergies.length > 0 ||
                    medications.length > 0 ||
                    addresses.length > 0 ||
                    permanencyStatuses.length > 0 ||
                    diagnoses.length > 0 ? (
                      <strong>
                        Select from the following records to additionally
                        expire:
                      </strong>
                    ) : null}
                    {clientIdentifiers.length > 0 && (
                      <div className="border border-success rounded p-2 mb-2">
                        <strong>Client Identifiers:</strong>
                        <div className="mt-2 mx-3">
                          {clientIdentifiers.map((identifier) => (
                            <Checkbox
                              key={identifier._id}
                              name={identifier.name}
                              label={identifier.name}
                              onChange={(e) => {
                                handleArrayChange(
                                  e,
                                  identifier,
                                  "clientIdentifiersToExpire"
                                );
                              }}
                            ></Checkbox>
                          ))}
                        </div>
                      </div>
                    )}
                    {clientPayors.length > 0 && (
                      <div className="border border-success rounded p-2 mb-2">
                        <strong>Client Payors:</strong>
                        <div className="mt-2 mx-3">
                          {clientPayors.map((payor) => (
                            <Checkbox
                              key={payor._id}
                              name={payor.name}
                              label={payor.name}
                              onChange={(e) => {
                                handleArrayChange(
                                  e,
                                  payor,
                                  "clientPayorsToExpire"
                                );
                              }}
                            ></Checkbox>
                          ))}
                        </div>
                      </div>
                    )}
                    {allergies.length > 0 && (
                      <div className="border border-success rounded p-2 mb-2">
                        <strong>Allergies:</strong>
                        <div className="mt-2 mx-3">
                          {allergies.map((allergy) => (
                            <Checkbox
                              key={allergy._id}
                              name={allergy.name}
                              label={allergy.name}
                              onChange={(e) => {
                                handleArrayChange(
                                  e,
                                  allergy,
                                  "allergiesToExpire"
                                );
                              }}
                            ></Checkbox>
                          ))}
                        </div>
                      </div>
                    )}
                    {medications.length > 0 && (
                      <div className="border border-success rounded p-2 mb-2">
                        <strong>Medications:</strong>
                        <div className="mt-2 mx-3">
                          {medications.map((med) => (
                            <Checkbox
                              key={med._id}
                              name={med.name}
                              label={med.name}
                              onChange={(e) => {
                                handleArrayChange(
                                  e,
                                  med,
                                  "medicationsToExpire"
                                );
                              }}
                            ></Checkbox>
                          ))}
                        </div>
                      </div>
                    )}
                    {addresses.length > 0 && (
                      <div className="border border-success rounded p-2 mb-2">
                        <strong>Addresses:</strong>
                        <div className="mt-2 mx-3">
                          {addresses.map((address) => (
                            <Checkbox
                              key={address._id}
                              name={address.name}
                              label={address.name}
                              onChange={(e) => {
                                handleArrayChange(
                                  e,
                                  address,
                                  "addressesToExpire"
                                );
                              }}
                            ></Checkbox>
                          ))}
                        </div>
                      </div>
                    )}
                    {permanencyStatuses.length > 0 && (
                      <div className="border border-success rounded p-2 mb-2">
                        <strong>Permanency Statuses:</strong>
                        <div className="mt-2 mx-3">
                          {permanencyStatuses.map((status) => (
                            <Checkbox
                              key={status._id}
                              name={status.name}
                              label={status.name}
                              onChange={(e) => {
                                handleArrayChange(
                                  e,
                                  status,
                                  "permanencyStatusesToExpire"
                                );
                              }}
                            ></Checkbox>
                          ))}
                        </div>
                      </div>
                    )}
                    {diagnoses.length > 0 && (
                      <div className="border border-success rounded p-2 mb-2">
                        <strong>Diagnoses:</strong>
                        <div className="mt-2 mx-3">
                          {diagnoses.map((dx, index) => (
                            <Checkbox
                              key={dx._id}
                              name={dx.name + index}
                              label={dx.name}
                              onChange={(e) => {
                                handleArrayChange(e, dx, "diagnosesToExpire");
                              }}
                            ></Checkbox>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-danger" onClick={handleHideModal}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (validate(dischargeData)) handleSave();
              }}
              disabled={
                Object.keys(errors).length !== 0 || !dischargeData.dischargeDate
              }
            >
              Submit
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default DischargeChecklistModal;
